import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, Download, CheckCircle2, XCircle, Clock, Award,
  ClipboardList, X, FileText, Trash2,
} from "lucide-react";

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

const DEFAULT_FORM = {
  cert_number: "",
  college: "",
  course_type: "BACHELOR OF",
  bachelor_of: "",
  field: "",
  start_date: "",
  end_date: "",
  grade: "A+",
  mode: "OFFLINE",
  attendance: "100%",
  duration_hours: "",
  performance_score: "",
  registration_number: "",
  university_roll: "",
  father_name: "",
  semester: "",
  year: "",
  issue_date: new Date().toISOString().slice(0, 10),
};

const COURSE_TYPE_OPTIONS = [
  "BACHELOR OF",
  "BACHELOR IN",
  "DIPLOMA OF",
  "DIPLOMA IN",
  "MASTER OF",
  "MASTER IN",
];

const isPPU = (c) => /PATA?LIPUTRA/i.test((c || "").replace(/\s+/g, ""));
const certYear = (d) => (/^\d{4}/.test(d || "") ? d.slice(0, 4) : String(new Date().getFullYear()));
const certPrefix = (college, issueDate) => (isPPU(college) ? `PMR/${certYear(issueDate)}/066/` : "PMR/26/");

export default function AdminApplications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [issueFor, setIssueFor] = useState(null);     // application object
  const [form, setForm] = useState(DEFAULT_FORM);
  const [issuing, setIssuing] = useState(false);
  const [manualNo, setManualNo] = useState(false);
  const fileInputs = useRef({});
  const attendanceInputs = useRef({});

  useEffect(() => { load(); }, [filter]);

  const ppu = issueFor ? isPPU(form.college) : false;
  const ppuYear = certYear(form.issue_date);
  useEffect(() => {
    if (!issueFor || manualNo) return;
    const existing = issueFor.certificate_no || "";
    const prefix = certPrefix(form.college, form.issue_date);
    if (existing.toUpperCase().startsWith(prefix)) {
      setForm((f) => ({ ...f, cert_number: existing.slice(prefix.length) }));
      return;
    }
    if (!ppu) { setForm((f) => ({ ...f, cert_number: "" })); return; }
    api.get(`/admin/certificates/next-ppu-number?issue_date=${form.issue_date || ""}`)
      .then(({ data }) => setForm((f) => ({ ...f, cert_number: String(data.next) })))
      .catch(() => toast.error("Could not fetch next certificate number"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueFor, ppu, ppuYear]);
  const load = async () => {
    const r = await api.get(`/admin/internship-applications${filter ? `?status=${filter}` : ""}`);
    setItems(r.data || []);
  };

  const updateStatus = async (id, status) => {
    if (status === "rejected" && !window.confirm("Reject and DELETE this application permanently?")) return;
    try {
      const { data } = await api.put(`/admin/internship-applications/${id}/status?status=${status}`);
      if (data?.deleted) {
        toast.success(`Application rejected and removed${data.email_sent ? " — email sent to student" : ""}`);
      } else {
        toast.success(`Marked ${status}${data?.email_sent ? " — email sent to student" : ""}`);
      }
      load();
    } catch { toast.error("Failed"); }
  };

  const uploadCert = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/internship-applications/${id}/certificate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Certificate uploaded");
      load();
    } catch { toast.error("Upload failed"); }
  };

  const uploadAttendance = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/internship-applications/${id}/attendance`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attendance sheet uploaded");
      load();
    } catch { toast.error("Attendance upload failed"); }
  };

  const removeAttendance = async (id) => {
    if (!window.confirm("Remove the uploaded attendance sheet?")) return;
    try {
      await api.delete(`/admin/internship-applications/${id}/attendance`);
      toast.success("Attendance removed");
      load();
    } catch { toast.error("Remove failed"); }
  };

  const openIssueModal = (a) => {
    setIssueFor(a);
    setManualNo(false);
    setForm({
      ...DEFAULT_FORM,
      cert_number: a.certificate_no?.replace(/^PMR\/(\d{4}\/066|26)\//i, "") || "",
      field: a.internship_title || "",
      // Auto-prefill college from the application (admin can override).
      college: a.college || "",
      // Registration number: use PMR Member ID if issued, else leave BLANK
      // so admin fills it manually. Do NOT default to university roll —
      // otherwise Registration Number and University Roll show the same
      // value on the certificate.
      registration_number: a.applicant_member_id || "",
      university_roll: a.university_roll_number || "",
      father_name: a.father_name || a.applicant_father_name || "",
      semester: a.semester || "",
      year: a.year || "",
      issue_date: new Date().toISOString().slice(0, 10),
    });
  };

  const submitIssue = async () => {
    if (!form.cert_number) { toast.error("Certificate number is required"); return; }
    setIssuing(true);
    try {
      const { data } = await api.post(
        `/admin/internship-applications/${issueFor.id}/auto-certificate`,
        form,
      );
      toast.success(`Certificate ${data.certificate_no} issued`);
      const es = data.email_status || {};
      if (es.sent) {
        toast.success("📧 Email sent to student");
      } else if (es.error) {
        toast.error(`Email NOT sent: ${es.error}. Check Resend domain/SENDER_EMAIL.`, { duration: 8000 });
      }
      setIssueFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue certificate");
    } finally { setIssuing(false); }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between">
        <h1 className="heading text-2xl font-bold">Applications</h1>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
          data-testid="apps-filter-select"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Applicant</th>
              <th className="p-3 font-semibold">Program</th>
              <th className="p-3 font-semibold">College</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold">Certificate</th>
              <th className="p-3 font-semibold">Attendance</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`app-row-${a.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.email} · {a.phone}</div>
                </td>
                <td className="p-3">{a.internship_title}</td>
                <td className="p-3 text-slate-600">{a.college}</td>
                <td className="p-3">
                  <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : a.status === "rejected" ? "bg-red-100 text-red-700" : a.status === "completed" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="p-3">
                  {a.certificate_url ? (
                    <a href={ABS(a.certificate_url)} target="_blank" rel="noreferrer" className="text-brand font-semibold inline-flex items-center gap-1">
                      <Download size={14} /> {a.certificate_no || "View"}
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="p-3">
                  {a.attendance_url ? (
                    <div className="inline-flex items-center gap-1.5">
                      <a href={ABS(a.attendance_url)} target="_blank" rel="noreferrer" className="text-brand font-semibold inline-flex items-center gap-1 text-xs">
                        <FileText size={13} /> Sheet
                      </a>
                      <button onClick={() => removeAttendance(a.id)} className="text-red-500 hover:text-red-700" title="Remove attendance" data-testid={`del-attend-${a.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1 items-center">
                    {a.status !== "approved" && (
                      <button onClick={() => updateStatus(a.id, "approved")} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded" title="Approve" data-testid={`approve-${a.id}`}><CheckCircle2 size={14} /></button>
                    )}
                    {a.status !== "rejected" && (
                      <button onClick={() => updateStatus(a.id, "rejected")} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Reject" data-testid={`reject-${a.id}`}><XCircle size={14} /></button>
                    )}
                    {a.status === "pending" && (
                      <button onClick={() => updateStatus(a.id, "pending")} className="p-2 text-amber-600 hover:bg-amber-50 rounded" title="Pending" data-testid={`pending-${a.id}`}><Clock size={14} /></button>
                    )}

                    {/* Attendance upload */}
                    <input
                      type="file"
                      accept=".pdf,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                      ref={(el) => (attendanceInputs.current[a.id] = el)}
                      onChange={(e) => e.target.files?.[0] && uploadAttendance(a.id, e.target.files[0])}
                      className="hidden"
                    />
                    <button onClick={() => attendanceInputs.current[a.id]?.click()} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded" title="Upload attendance sheet" data-testid={`upload-attend-${a.id}`}>
                      <ClipboardList size={14} />
                    </button>

                    {/* Manual certificate file upload */}
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      ref={(el) => (fileInputs.current[a.id] = el)}
                      onChange={(e) => e.target.files?.[0] && uploadCert(a.id, e.target.files[0])}
                      className="hidden"
                    />
                    <button onClick={() => fileInputs.current[a.id]?.click()} className="p-2 text-brand hover:bg-blue-50 rounded" title="Upload external certificate file" data-testid={`upload-cert-${a.id}`}>
                      <Upload size={14} />
                    </button>

                    {/* Issue certificate via modal — only enabled when approved */}
                    <button onClick={() => openIssueModal(a)} disabled={!["approved", "completed"].includes(a.status)} className="p-2 text-[#F59E0B] hover:bg-amber-50 disabled:opacity-30 disabled:cursor-not-allowed rounded" title={["approved", "completed"].includes(a.status) ? "Issue / Generate certificate" : "Approve the application first"} data-testid={`auto-cert-${a.id}`}>
                      <Award size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-500">No applications.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Issue Certificate Modal */}
      {issueFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="issue-cert-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mt-10">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="heading text-lg font-black">Issue Certificate</h2>
                <p className="text-xs text-slate-500 mt-0.5">For {issueFor.name} · {issueFor.email}</p>
              </div>
              <button onClick={() => setIssueFor(null)} className="text-slate-500 hover:text-slate-900" data-testid="close-issue-modal"><X size={20} /></button>
            </div>

            <div className="px-6 py-5 grid grid-cols-2 gap-4">
              <Field label="Certificate Number *" hint={ppu ? "Patliputra University — next number auto-filled, you can edit it" : "Will be saved as PMR/26/<value>"}>
                <div className="flex">
                  <span className="px-3 py-2 bg-slate-100 text-slate-600 text-sm rounded-l-lg border border-r-0 border-slate-300 font-mono" data-testid="cert-number-prefix">{certPrefix(form.college, form.issue_date)}</span>
                  <input
                    value={form.cert_number}
                    onChange={(e) => { setForm({ ...form, cert_number: e.target.value }); setManualNo(true); }}
                    placeholder={ppu ? "1" : "045"}
                    className="flex-1 px-3 py-2 rounded-r-lg border border-slate-300 text-sm font-mono"
                    data-testid="cert-number-input"
                  />
                </div>
              </Field>

              <Field label="Issue Date">
                <input type="date" value={form.issue_date}
                  onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="issue-date-input" />
              </Field>

              <Field label="College / University (FROM)" full hint="Appears next to the FROM ___ line on the certificate">
                <input value={form.college} placeholder="DELHI UNIVERSITY"
                  onChange={(e) => setForm({ ...form, college: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="college-input" />
              </Field>

              <Field label="Course Type" hint="STUDYING IN ___ prefix on certificate">
                <select
                  value={form.course_type}
                  onChange={(e) => setForm({ ...form, course_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                  data-testid="course-type-select"
                >
                  {COURSE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </Field>

              <Field label="Course Name" hint="ARTS / SCIENCE / COMPUTER APPLICATIONS etc.">
                <input value={form.bachelor_of} placeholder="ARTS / SCIENCE / COMMERCE"
                  onChange={(e) => setForm({ ...form, bachelor_of: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Field of Internship">
                <input value={form.field} placeholder="WOMEN EMPOWERMENT"
                  onChange={(e) => setForm({ ...form, field: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Start Date">
                <input type="date" value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="End Date">
                <input type="date" value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Grade">
                <input value={form.grade}
                  onChange={(e) => setForm({ ...form, grade: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Mode">
                <select value={form.mode}
                  onChange={(e) => setForm({ ...form, mode: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
                  <option>OFFLINE</option>
                  <option>ONLINE</option>
                  <option>HYBRID</option>
                </select>
              </Field>

              <Field label="Attendance">
                <input value={form.attendance}
                  onChange={(e) => setForm({ ...form, attendance: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Duration (Hours)">
                <input value={form.duration_hours} placeholder="80"
                  onChange={(e) => setForm({ ...form, duration_hours: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Performance Score (out of 100)">
                <input value={form.performance_score} placeholder="95"
                  onChange={(e) => setForm({ ...form, performance_score: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Registration Number">
                <input value={form.registration_number}
                  onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Father's Name">
                <input value={form.father_name}
                  onChange={(e) => setForm({ ...form, father_name: e.target.value })}
                  placeholder="(optional — leave blank to hide row)"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="cert-father-name-input" />
              </Field>

              <Field label="Semester" hint="Leave blank if using Year instead">
                <input value={form.semester}
                  onChange={(e) => setForm({ ...form, semester: e.target.value })}
                  placeholder='e.g. "5th" or "VI"'
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="cert-semester-input" />
              </Field>

              <Field label="Year" hint="Leave blank if using Semester instead">
                <input value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  placeholder='e.g. "3rd" or "III"'
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="cert-year-input" />
              </Field>

              <Field label="University Roll Number" full>
                <input value={form.university_roll}
                  onChange={(e) => setForm({ ...form, university_roll: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-xs text-slate-500">QR will point to <span className="font-mono">pmrwelfarefoundation.org/verify</span></p>
              <div className="flex gap-2">
                <button onClick={() => setIssueFor(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">Cancel</button>
                <button onClick={submitIssue} disabled={issuing} className="px-4 py-2 rounded-lg bg-[#F59E0B] hover:bg-[#D97706] text-white text-sm font-black disabled:opacity-50" data-testid="submit-issue-cert">
                  {issuing ? "Issuing..." : "Issue Certificate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, hint, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[11px] font-black uppercase tracking-wider text-slate-600">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}
